import { onBeforeUnmount, onMounted } from 'vue'

/**
 * useModalHeightAnimation
 *
 * Smoothly animate the height of every AntDesign dialog (`.ant-modal-content`)
 * when its content height changes — switching tabs, revealing sections, async
 * data that lands and resizes the dialog. Without it the dialog snaps to the
 * next height in a single frame, which reads as a jarring jump on tab switches
 * and content swaps.
 *
 * Implementation notes
 * - A `MutationObserver` runs as a microtask, i.e. after the DOM changed but
 *   *before* the browser lays out and paints. There we pin the dialog to its
 *   previous height, measure the natural height, then transition to it on the
 *   next frame. Pinning before paint is what keeps the new height from flashing
 *   for a frame before the animation starts (a plain ResizeObserver fires after
 *   layout, which does flash).
 * - A `ResizeObserver` only keeps the baseline height in sync for size changes
 *   that are not caused by DOM mutations (e.g. an image finishing loading);
 *   those are applied instantly.
 */
const DURATION = 200
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)'
// Skip tiny fluctuations (hover, sub-pixel reflow) so ordinary layout noise
// does not trigger an animation.
const MIN_DELTA = 8
const SELECTOR = '.ant-modal-content'

export function useModalHeightAnimation() {
  let resizeObserver: ResizeObserver | null = null
  let mountObserver: MutationObserver | null = null
  const baseline = new WeakMap<HTMLElement, number>()
  const animating = new WeakSet<HTMLElement>()
  const sessions = new WeakMap<HTMLElement, object>()
  const contentObservers = new WeakMap<HTMLElement, MutationObserver>()

  function heightOf(el: HTMLElement) {
    return el.offsetHeight
  }

  function settled(el: HTMLElement) {
    animating.delete(el)
    baseline.set(el, heightOf(el))
  }

  function play(el: HTMLElement, from: number, to: number) {
    animating.add(el)
    const session = {}
    sessions.set(el, session)
    el.style.height = `${from}px`
    el.style.overflow = 'hidden'
    el.style.transition = 'none'

    requestAnimationFrame(() => {
      if (sessions.get(el) !== session) return
      if (!el.isConnected) return settled(el)
      el.style.transition = `height ${DURATION}ms ${EASING}`
      el.style.height = `${to}px`

      const finish = (event?: TransitionEvent) => {
        if (sessions.get(el) !== session) return
        // Ignore transitionend bubbling up from descendants.
        if (event && event.target !== el) return
        sessions.delete(el)
        el.removeEventListener('transitionend', finish)
        el.style.transition = ''
        el.style.height = ''
        el.style.overflow = ''
        // Content may have changed again while animating; catch up if so.
        const now = heightOf(el)
        if (Math.abs(now - to) >= MIN_DELTA) run(el)
        else settled(el)
      }
      el.addEventListener('transitionend', finish)
      window.setTimeout(() => finish(), DURATION + 120)
    })
  }

  function run(el: HTMLElement) {
    if (animating.has(el) || !el.isConnected || el.getClientRects().length === 0) return
    const prev = baseline.get(el) ?? heightOf(el)
    // Measure the natural height (no explicit height allowed to interfere).
    el.style.height = ''
    const natural = heightOf(el)
    if (natural < 1) return
    baseline.set(el, natural)
    if (Math.abs(natural - prev) < MIN_DELTA) return
    play(el, prev, natural)
  }

  function observe(el: Element) {
    const node = el as HTMLElement
    if (baseline.has(node)) return
    baseline.set(node, heightOf(node))
    resizeObserver?.observe(node)
    const observer = new MutationObserver(() => run(node))
    observer.observe(node, { childList: true, subtree: true })
    contentObservers.set(node, observer)
  }

  function release(el: Element) {
    const node = el as HTMLElement
    resizeObserver?.unobserve(node)
    contentObservers.get(node)?.disconnect()
    contentObservers.delete(node)
    baseline.delete(node)
  }

  function scan(node: HTMLElement) {
    if (node.matches(SELECTOR)) observe(node)
    node.querySelectorAll(SELECTOR).forEach(observe)
  }

  function releaseTree(node: HTMLElement) {
    if (node.matches(SELECTOR)) release(node)
    node.querySelectorAll(SELECTOR).forEach(release)
  }

  onMounted(() => {
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const el = entry.target as HTMLElement
        if (!animating.has(el)) baseline.set(el, heightOf(el))
      }
    })
    scan(document.body)
    // Dialogs are portaled into <body>; watch for those mounted or removed.
    mountObserver = new MutationObserver((records) => {
      for (const record of records) {
        record.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) scan(node)
        })
        record.removedNodes.forEach((node) => {
          if (node instanceof HTMLElement) releaseTree(node)
        })
      }
    })
    mountObserver.observe(document.body, { childList: true })
  })

  onBeforeUnmount(() => {
    resizeObserver?.disconnect()
    mountObserver?.disconnect()
  })
}
